import React, { useEffect, useMemo, useCallback, useState } from "react";
import ReactPaginate from "react-paginate";
import api from "../../axiosConfig";
import {
  Table,
  Button,
  InputGroup,
  FormControl,
  Container,
} from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import "../pagination.css";

function AccountsList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [accounts, setAccounts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // ✅ URL is the single source of truth
  const page = useMemo(
    () => parseInt(searchParams.get("page") ?? "0", 10),
    [searchParams],
  );
  const limit = useMemo(
    () => parseInt(searchParams.get("limit") ?? "10", 10),
    [searchParams],
  );
  const q = useMemo(() => searchParams.get("q") || "", [searchParams]);

  const updateQuery = useCallback(
    (updates) => {
      const sp = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([k, v]) => {
        if (v === null || v === undefined || v === "") sp.delete(k);
        else sp.set(k, String(v));
      });
      setSearchParams(sp, { replace: false });
    },
    [searchParams, setSearchParams],
  );

  // ✅ optional: debounce search input (prevents a request on every keystroke)
  const [searchInput, setSearchInput] = useState(q);
  useEffect(() => setSearchInput(q), [q]);

  useEffect(() => {
    const t = setTimeout(() => {
      // when user types, reset to page 0
      if (searchInput !== q) {
        updateQuery({ q: searchInput || null, page: 0, limit });
      }
    }, 100);
    return () => clearTimeout(t);
  }, [searchInput, q, updateQuery, limit]);

  const fetchAccounts = useCallback(async () => {
    const skip = page * limit;
    try {
      const res = await api.get("/accounts", {
        params: { search: q, skip, limit },
      });
      setAccounts(res.data.items ?? []);
      setTotalCount(res.data.total ?? 0);
    } catch (err) {
      console.error("Eroare la încărcarea conturilor:", err);
    }
  }, [q, page, limit]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const handleDelete = async (id) => {
    if (!window.confirm(`Sigur dorești să ștergi contul cu ID ${id}?`)) return;
    try {
      await api.delete(`/accounts/${id}`);
      fetchAccounts();
    } catch {
      alert("Eroare la ștergere.");
    }
  };

  const pageCount = Math.max(1, Math.ceil(totalCount / limit));

  const handlePageChange = (event) => {
    updateQuery({ page: event.selected, limit, q: q || null });
  };

  const handlePerPageChange = (e) => {
    const n = parseInt(e.target.value, 10);
    updateQuery({ limit: n, page: 0, q: q || null });
  };

  return (
    <Container className="mt-4">
      <h2>Lista conturi</h2>

      <div className="d-flex flex-wrap justify-content-end gap-2 mb-3">
        <Button variant="outline-secondary" onClick={() => navigate("/")}>Înapoi</Button>
        <Button onClick={() => navigate("/accounts/create")}>Adaugă cont</Button>
      </div>

      <InputGroup className="mb-3">
        <FormControl
          placeholder="Caută după denumire"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        {q && (
          <Button
            variant="outline-danger"
            onClick={() => updateQuery({ q: null, page: 0, limit })}
          >
            Curăță
          </Button>
        )}
      </InputGroup>

      <div className="mb-3">
        <label>
          Rânduri pe pagină:&nbsp;
          <select value={limit} onChange={handlePerPageChange}>
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </label>
      </div>

      <Table striped bordered hover>
        <thead>
          <tr>
            <th>ID</th>
            <th>Cont</th>
            <th>Denumire</th>
            <th>Acțiuni</th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((account) => (
            <tr key={account.id}>
              <td>{account.id}</td>
              <td>{account.code}</td>
              <td>{account.name}</td>
              <td>
                <Button
                  size="sm"
                  onClick={() =>
                    navigate(
                      `/accounts/${account.id}?${searchParams.toString()}`,
                    )
                  }
                >
                  Editează
                </Button>{" "}
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleDelete(account.id)}
                >
                  Șterge
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <ReactPaginate
        previousLabel={"← Înapoi"}
        nextLabel={"Înainte →"}
        pageCount={pageCount}
        forcePage={page}
        onPageChange={handlePageChange}
        containerClassName={"pagination justify-content-center"}
        pageClassName={"page-item"}
        pageLinkClassName={"page-link"}
        previousClassName={"page-item"}
        previousLinkClassName={"page-link"}
        nextClassName={"page-item"}
        nextLinkClassName={"page-link"}
        activeClassName={"active"}
      />
    </Container>
  );
}

export default AccountsList;
